import { useTranslation } from 'react-i18next'
import { Loading, Typogaphy } from 'vnetwork-ui'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getCustomersByProduct } from '../services'
import { CustomerPipelineFilters } from '../types'
import { Info } from 'lucide-react'
import {
    Tooltip as UITooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip'
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    Cell,
    ReferenceLine,
} from 'recharts'
import { SCHEMES } from '@/components/EchartCustomized'

interface ProductBarChartProps {
    filters: CustomerPipelineFilters
}

/**
 * Abbreviate long product names to acronyms
 * e.g., "CONTENT DELIVERY NETWORK" -> "CDN"
 *       "CLOUD SERVICE" -> "Cloud Service" (if short enough)
 */
const abbreviateProductName = (name: string, maxLen = 12): string => {
    if (!name) return 'Unknown'

    // If already short enough, capitalize properly
    if (name.length <= maxLen) {
        return name
            .toLowerCase()
            .split(' ')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' ')
    }

    // Create acronym from words
    const words = name.trim().split(/\s+/)
    if (words.length >= 2) {
        const acronym = words.map((w) => w.charAt(0).toUpperCase()).join('')
        return acronym
    }

    // Single long word - truncate
    return name.slice(0, maxLen) + '...'
}

// Color palette for bars
const BAR_COLORS = [
    '#8B5CF6', // purple
    '#F87171', // red
    '#34D399', // green
    '#FBBF24', // yellow
    '#60A5FA', // blue
    '#F97316', // orange
    '#EC4899', // pink
    '#14B8A6', // teal
    '#A78BFA', // light purple
    '#6EE7B7', // light green
]

const ProductBarChart: React.FC<ProductBarChartProps> = ({ filters }) => {
    const { t } = useTranslation()

    const { data, renderState } = useQueryWithHandler(
        {
            queryKey: [
                QUERY_KEYS.REPORTS_SALE_PERFORMANCES,
                'by-product',
                filters.gte,
                filters.lte,
                filters.sales_manager_codes,
            ],
            queryFn: () => getCustomersByProduct(filters),
            enabled: !!filters.gte && !!filters.lte,
        },
        {
            loadingComponent: (
                <div className="bg-white rounded-lg p-4 border border-gray-100 shadow-sm">
                    <div className="flex justify-center items-center h-[300px]">
                        <Loading.CircularProgress width={32} height={32} />
                    </div>
                </div>
            ),
        }
    )

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    const products = data?.data || []

    // Calculate average for reference line
    const avgValue =
        products.length > 0
            ? Math.round(
                  products.reduce((sum, item) => sum + item.count, 0) /
                      products.length
              )
            : 0

    // Transform data for recharts
    const chartData = products.map((item, index) => ({
        product: item.product || 'Unknown',
        count: item.count,
        color: BAR_COLORS[index % BAR_COLORS.length],
    }))

    return (
        <div className="bg-white rounded-md p-4 border border-gray-100 shadow-sm">
            <div className="flex items-center justify-between mb-2">
                <Typogaphy className="font-semibold text-primary capitalize">
                    {t('customer_summary.customers_by_product')}
                </Typogaphy>
                <TooltipProvider>
                    <UITooltip>
                        <TooltipTrigger asChild>
                            <button className="text-gray-400 hover:text-gray-600">
                                <Info className="w-4 h-4" />
                            </button>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p className="max-w-xs text-xs">
                                {t(
                                    'customer_summary.customers_by_product_tooltip'
                                )}
                            </p>
                        </TooltipContent>
                    </UITooltip>
                </TooltipProvider>
            </div>

            {products?.length === 0 ? (
                <div className="flex items-center justify-center h-[280px] text-gray-400 text-sm">
                    {t('no_data')}
                </div>
            ) : (
                <div style={{ height: 500 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                            data={chartData}
                            margin={{
                                top: 10,
                                right: 30,
                                bottom: 60,
                                left: 10,
                            }}
                        >
                            <CartesianGrid
                                strokeDasharray="3 3"
                                stroke="#e5e7eb"
                            />
                            <XAxis
                                dataKey="product"
                                tick={({ x, y, payload }) => (
                                    <g transform={`translate(${x},${y})`}>
                                        <text
                                            x={0}
                                            y={0}
                                            dy={10}
                                            textAnchor="end"
                                            fill="#666"
                                            fontSize={10}
                                            transform="rotate(-45)"
                                            style={{
                                                whiteSpace: 'nowrap',
                                                textTransform: 'uppercase',
                                            }}
                                        >
                                            {abbreviateProductName(
                                                payload.value,
                                                20
                                            )}
                                        </text>
                                    </g>
                                )}
                                interval={0}
                                height={20}
                            />
                            <YAxis tick={{ fontSize: 11 }} />
                            <Tooltip
                                contentStyle={{
                                    backgroundColor: 'white',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '8px',
                                    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                                }}
                                formatter={(value: number | undefined) => [
                                    value?.toLocaleString() || '',
                                    t('customer_summary.customers'),
                                ]}
                            />
                            {/* Average reference line */}
                            <ReferenceLine
                                y={avgValue}
                                stroke={SCHEMES.primary}
                                strokeDasharray="5 5"
                                strokeWidth={2}
                                label={{
                                    value: `TB: ${avgValue}`,
                                    position: 'right',
                                    fontSize: 10,
                                    fill: SCHEMES.primary,
                                }}
                            />
                            <Bar
                                dataKey="count"
                                radius={[2, 2, 0, 0]}
                                barSize={60}
                            >
                                {chartData.map((entry, index) => (
                                    <Cell
                                        key={`cell-${index}`}
                                        fill={entry.color}
                                    />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    )
}

export default ProductBarChart
